import type {
  AgentDocUpdate,
  ClientActionRef,
} from '@streamlain/copilot-realtime-contract';
import { isServerDatabaseRowShardDocId } from '@streamlain/database-identity';
import type { DocClock } from '@streamlain/doc-publication-contract';
import { PublicationError } from '@streamlain/doc-publication-contract';
import { isEmptyUpdate } from '@streamlain/doc-publication-contract';
import type {
  WorkspacePermissionGraphAdvancedPayload,
  WorkspaceSearchAdvancedPayload,
} from '@streamlain/permission-snapshot-contract';
import EventEmitter2 from 'eventemitter2';
import {
  applyUpdate,
  decodeUpdate,
  diffUpdate,
  Doc as YDoc,
  encodeStateAsUpdate,
  encodeStateVectorFromUpdate,
  Item,
  mergeUpdates,
} from 'yjs';

import type { Connection } from '../connection';
import type { PermissionsChangedEvent } from '../sync/doc/peer-types';
import { isQuotaExceededError } from '../utils/quota';
import type { Locker } from './lock';
import { SingletonLocker } from './lock';
import { type Storage } from './storage';

// Amortize materialization at the snapshot boundary, never per incoming delta.
// This is a compaction trigger, not a document or transport admission limit.
const SNAPSHOT_GC_MIN_BYTES = 512 * 1024;

export type { DocClock };

export type DocClocks = Record<string, Date>;
export interface DocRecord extends DocClock {
  clientAction?: ClientActionRef;
  bin: Uint8Array;
  editor?: string;
  /** The agent step that made a live server update; never stored. */
  agent?: AgentDocUpdate;
}

export interface DocDiff extends DocClock {
  missing: Uint8Array;
  state: Uint8Array;
}

export interface DocUpdate {
  clientAction?: ClientActionRef;
  docId: string;
  bin: Uint8Array;
  editor?: string;
  requestId?: string;
  /** The agent step that made a live server update; never stored. */
  agent?: AgentDocUpdate;
}

export interface Editor {
  name: string;
  avatarUrl: string | null;
}

export interface DocCorruptionEvent {
  type: 'snapshot' | 'update';
  docId: string;
  timestamp?: Date;
  expected: string;
  actual: string;
}

export interface CorruptionReport {
  docId: string;
  timestamp: string;
  error: string;
  binLength: number;
  binPreview: number[];
  docTimestamp?: Date;
}

export interface DemoContentRemovedEvent {
  workspaceId: string;
  docId: string;
}

/** The server refused the join: it no longer speaks this client's sync protocol. */
export interface PageFormatUpgradeRefusedEvent {
  workspaceId: string;
  docId: string;
  reason: string;
  timestamp: number;
}

export interface SyncProtocolUnsupportedEvent {
  workspaceId: string;
  serverProtocol: number;
  minProtocol: number;
  timestamp: number;
}

/** Access bindings for these database system docs are committed server-side. */
export interface SystemDocAccessReadyEvent {
  workspaceId: string;
  docIds: string[];
  timestamp: number;
}

/** A write of this doc was persisted; `timestamp` is its stored snapshot clock. */
export interface DocPersistedEvent {
  docId: string;
  timestamp: Date;
}

export interface DocStorageOptions {
  mergeUpdates?: (updates: Uint8Array[]) => Promise<Uint8Array> | Uint8Array;
  id: string;

  /**
   * open as readonly mode.
   */
  readonlyMode?: boolean;
}

/** Recovery version the local copy of a space was built on. */
export interface RecoveryVersionStore {
  /**
   * null: no local copy yet. 0: a local copy that recorded no version, which
   * is current only while the workspace was never recovered.
   */
  getRecoveryVersion(): Promise<number | null>;
  setRecoveryVersion(version: number): Promise<void>;
}

export function isRecoveryVersionStore(
  storage: unknown
): storage is RecoveryVersionStore {
  return (
    typeof storage === 'object' &&
    storage !== null &&
    typeof (storage as RecoveryVersionStore).getRecoveryVersion ===
      'function' &&
    typeof (storage as RecoveryVersionStore).setRecoveryVersion === 'function'
  );
}

export interface DocStorage extends Storage {
  readonly storageType: 'doc';
  readonly isReadonly: boolean;
  readonly spaceId: string;
  /**
   * Get a doc record with latest binary.
   */
  getDoc(docId: string): Promise<DocRecord | null>;
  /**
   * Bytes this storage holds for a doc: its snapshot and pending updates as
   * stored, 0 when the doc is not stored.
   */
  getDocStoredBytes(docId: string): Promise<number>;
  /**
   * Get a yjs binary diff with the given state vector.
   */
  getDocDiff(docId: string, state?: Uint8Array): Promise<DocDiff | null>;
  /**
   * Push updates into storage
   *
   * @param origin - Internal identifier to recognize the source in the "update" event. Will not be stored or transferred.
   */
  pushDocUpdate(update: DocUpdate, origin?: string): Promise<DocClock>;

  /**
   * Get the timestamp of the latest update of a doc.
   */
  getDocTimestamp(docId: string): Promise<DocClock | null>;

  /**
   * Get all docs timestamps info. especially for useful in sync process.
   */
  getDocTimestamps(after?: Date, limit?: number): Promise<DocClocks>;

  /**
   * Delete a specific doc data with all snapshots and updates
   */
  deleteDoc(docId: string): Promise<void>;

  /**
   * Delete only the local storage copy for a doc.
   *
   * This must not cascade to remote storage. Use it for local cache/draft
   * cleanup paths where the server either already owns the canonical state or
   * may not know the doc yet.
   */
  resetLocalDoc?(docId: string): Promise<void>;

  /**
   * Replace only the local copy of one doc with a confirmed canonical record.
   *
   * Implementations must swap snapshot, pending updates, and clock atomically.
   * This is intentionally optional: callers must not attempt local poison
   * rebuilds when the active local storage cannot provide atomic replacement.
   */
  replaceLocalDoc?(record: DocRecord, origin?: string): Promise<DocClock>;

  /**
   * Subscribe on doc updates emitted from storage itself.
   *
   * NOTE:
   *
   *   There is not always update emitted from storage itself.
   *
   *   For example, in Sqlite storage, the update will only come from user's updating on docs,
   *   in other words, the update will never somehow auto generated in storage internally.
   *
   *   But for Cloud storage, there will be updates broadcasted from other clients,
   *   so the storage will emit updates to notify the client to integrate them.
   */
  subscribeDocUpdate(
    callback: (update: DocRecord, origin?: string) => void
  ): () => void;

  /**
   * sl-affine-uhr: Subscribe to compaction events.
   * Called when a doc has been compacted on the server.
   * Clients should clear local cache and pull fresh state.
   */
  subscribeDocCompacted(
    callback: (info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }) => void
  ): () => void;

  /**
   * sl-j143.5: Subscribe to permission change events.
   * Called when the user's doc/folder permissions change on the server.
   */
  subscribePermissionsChanged(
    callback: (info: PermissionsChangedEvent) => void
  ): () => void;

  /** Counter signal for a permission change that does not affect this user. */
  subscribePermissionGraphAdvanced(
    callback: (info: WorkspacePermissionGraphAdvancedPayload) => void
  ): () => void;

  subscribeDemoContentRemoved(
    callback: (info: DemoContentRemovedEvent) => void
  ): () => void;

  /** The server refused the join for this client's sync protocol. */
  subscribeSyncProtocolUnsupported(
    callback: (info: SyncProtocolUnsupportedEvent) => void
  ): () => void;

  /**
   * Subscribe to server notifications that access bindings for database
   * system docs (root / row shards) were committed. Docs isolated with
   * PermissionLag can retry immediately instead of waiting for backoff.
   */
  subscribeSystemDocAccessReady(
    callback: (info: SystemDocAccessReadyEvent) => void
  ): () => void;

  /**
   * Subscribe to the stored snapshot clock the server reports after it
   * persisted a write of a database system doc (root / row shard).
   */
  subscribeDocPersisted(
    callback: (info: DocPersistedEvent) => void
  ): () => void;

  /**
   * Subscribe to workspace meta change events.
   * Called when workspace metadata changes (e.g. folder_deleted).
   * Propagates workspace:meta-changed WebSocket events through the sync chain.
   */
  subscribeWorkspaceMetaChanged(
    callback: (info: {
      workspaceId: string;
      changeType: string;
      docId?: string | null;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }) => void
  ): () => void;

  /**
   * sl-lkdb.3.1: Subscribe to recovery completed events.
   * Called when PITR recovery completes — clients should clear IDB and re-sync.
   */
  subscribeRecoveryCompleted(
    callback: (info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }) => void
  ): () => void;

  /**
   * sl-lv2w.5: Subscribe to doc size warning events (soft limit).
   * Called when a doc crosses the size warning threshold.
   */
  subscribeDocSizeWarning(
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }) => void
  ): () => void;

  /**
   * sl-lv2w.5: Subscribe to doc size hard limit events.
   * Called when a doc crosses the hard size limit — new inserts should be blocked.
   */
  subscribeDocSizeHardLimit(
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }) => void
  ): () => void;

  /**
   * sl-00ro: Subscribe to embedding doc status events.
   */
  subscribeEmbeddingDocStatus(
    callback: (info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }) => void
  ): () => void;

  /**
   * sl-00ro: Subscribe to embedding workspace progress events.
   */
  subscribeEmbeddingWorkspaceProgress(
    callback: (info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }) => void
  ): () => void;

  /**
   * Subscribe to knowledge graph update events.
   */
  subscribeKgUpdated(
    callback: (info: {
      workspaceId: string;
      entityCount: number;
      relationCount: number;
      timestamp: number;
    }) => void
  ): () => void;

  /** Subscribe to new search index versions of readable pages. */
  subscribeSearchAdvanced(
    callback: (info: WorkspaceSearchAdvancedPayload) => void
  ): () => void;

  /**
   * Surgically repair a corrupt doc by removing only corrupt IDB entries.
   * Validates snapshot and each individual update, deletes only corrupt ones.
   * Preserves all valid unsynced data — safe for workspace root and all docs.
   */
  repairDoc?(docId: string): Promise<DocRepairResult>;
}

export interface DocRepairResult {
  /** Number of valid update entries kept */
  kept: number;
  /** Number of corrupt update entries removed */
  removed: number;
  /** Whether the snapshot was corrupt and had to be rebuilt/removed */
  snapshotRemoved: boolean;
  /** Whether the doc is now readable after repair */
  docReadable: boolean;
}

export abstract class DocStorageBase<Opts = {}> implements DocStorage {
  get isReadonly(): boolean {
    return this.options.readonlyMode ?? false;
  }
  private readonly event = new EventEmitter2();
  readonly storageType = 'doc';
  abstract readonly connection: Connection;
  protected readonly locker: Locker = new SingletonLocker();
  readonly spaceId = this.options.id;

  constructor(protected readonly options: Opts & DocStorageOptions) {}

  async getDoc(docId: string) {
    return this.readDocWithCompaction(docId);
  }

  async getDocStoredBytes(docId: string) {
    const { snapshot, updates } = await this.getDocParts(docId);
    return updates.reduce(
      (bytes, update) => bytes + update.bin.byteLength,
      snapshot?.bin.byteLength ?? 0
    );
  }

  /** Shared snapshot-CAS maintenance; publication readers may compact small
   * complete snapshots too, so superseded values do not accumulate per write. */
  protected async readDocWithCompaction(docId: string, compactSmall = false) {
    await using _lock = this.isReadonly
      ? undefined
      : await this.lockDocForUpdate(docId);

    const { snapshot, updates } = await this.getDocParts(docId);

    const merged = updates.length
      ? await this.squash(snapshot ? [snapshot, ...updates] : updates)
      : snapshot;
    if (!merged) return null;
    const bin = this.compactCompleteSnapshot(
      merged.bin,
      compactSmall,
      isServerDatabaseRowShardDocId(docId, this.spaceId)
    );

    if (updates.length || bin !== merged.bin) {
      const { timestamp, editor } = merged;

      const newSnapshot = {
        spaceId: this.spaceId,
        docId,
        bin,
        timestamp,
        editor,
      };

      // if is readonly, we will not set the new snapshot
      if (!this.isReadonly) {
        try {
          const stored = await this.setDocSnapshot(newSnapshot, snapshot);
          // A rejected snapshot write must never discard its unmerged updates.
          if (stored) await this.markUpdatesMerged(docId, updates);
        } catch (error) {
          if (!isQuotaExceededError(error)) {
            throw error;
          }

          console.warn(
            `[DocStorage] Skipped snapshot compaction for ${docId} because local IndexedDB quota is exceeded. Unmerged updates are preserved.`
          );
        }
      }

      return newSnapshot;
    }

    return snapshot;
  }

  /** GC only a captured snapshot plus all captured deltas under the doc lock.
   * Keep unresolved deltas byte-for-byte until their causal basis arrives.
   * Client clocks/delete sets survive GC; this is not a document reconstruction.
   */
  private compactCompleteSnapshot(
    bin: Uint8Array,
    compactSmall = false,
    rowShard = false
  ): Uint8Array {
    if (!compactSmall && bin.byteLength < SNAPSHOT_GC_MIN_BYTES) {
      // Small row snapshots need maintenance only when deleted content remains.
      // Do not allocate a Y.Doc for already collected tombstones or live data.
      if (!rowShard || bin.byteLength < 32 * 1024) return bin;
      const { structs, ds } = decodeUpdate(bin);
      const collectible = structs.some(
        struct =>
          struct instanceof Item &&
          struct.content.getRef() !== 1 &&
          ds.clients
            .get(struct.id.client)
            ?.some(
              range =>
                struct.id.clock < range.clock + range.len &&
                struct.id.clock + struct.length > range.clock
            )
      );
      if (!collectible) return bin;
    }
    const doc = new YDoc();
    try {
      applyUpdate(doc, bin);
      if (doc.store.pendingStructs || doc.store.pendingDs) return bin;
      const compacted = encodeStateAsUpdate(doc);
      return compacted.byteLength < bin.byteLength ? compacted : bin;
    } finally {
      doc.destroy();
    }
  }

  async getDocDiff(docId: string, state?: Uint8Array) {
    const doc = await this.getDoc(docId);

    if (!doc) {
      return null;
    }

    try {
      return {
        docId,
        missing:
          state && state.length > 0 ? diffUpdate(doc.bin, state) : doc.bin,
        state: encodeStateVectorFromUpdate(doc.bin),
        timestamp: doc.timestamp,
      };
    } catch (error) {
      // Local doc data is corrupt - preserve for analysis, don't delete!
      // Deletion would cause data loss in offline/local-first scenarios
      const errorMsg = error instanceof Error ? error.message : String(error);
      const corruptionReport = {
        docId,
        timestamp: new Date().toISOString(),
        error: errorMsg,
        binLength: doc.bin.length,
        binPreview: Array.from(doc.bin.slice(0, 50)),
        docTimestamp: doc.timestamp,
      };
      console.error(
        `[DocStorage] CORRUPT DATA DETECTED - preserving for analysis:`,
        JSON.stringify(corruptionReport)
      );
      // Store corruption report for later analysis
      this.storeCorruptionReport(corruptionReport).catch(() => {});
      // Re-throw to let caller handle (don't silently delete data!)
      throw new Error(`Corrupt local data for doc ${docId}: ${errorMsg}`);
    }
  }

  abstract pushDocUpdate(update: DocUpdate, origin?: string): Promise<DocClock>;

  abstract getDocTimestamp(docId: string): Promise<DocClock | null>;

  abstract getDocTimestamps(after?: Date, limit?: number): Promise<DocClocks>;

  abstract deleteDoc(docId: string): Promise<void>;

  /**
   * Store corruption report for analysis. Implementations should persist this
   * and implement log rotation (max 100 reports, FIFO).
   */
  protected async storeCorruptionReport(
    report: CorruptionReport
  ): Promise<void> {
    // Default implementation uses localStorage with rotation
    const STORAGE_KEY = `affine:corruption-reports:${this.spaceId}`;
    const MAX_REPORTS = 100;

    try {
      const existing = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || '[]'
      ) as CorruptionReport[];
      existing.push(report);
      // FIFO rotation - keep only last MAX_REPORTS
      while (existing.length > MAX_REPORTS) {
        existing.shift();
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    } catch {
      // localStorage might not be available (worker context)
      // Subclasses can override with IndexedDB implementation
    }
  }

  /**
   * Get stored corruption reports for analysis.
   */
  getCorruptionReports(): CorruptionReport[] {
    const STORAGE_KEY = `affine:corruption-reports:${this.spaceId}`;
    try {
      return JSON.parse(
        localStorage.getItem(STORAGE_KEY) || '[]'
      ) as CorruptionReport[];
    } catch {
      return [];
    }
  }

  subscribeDocUpdate(callback: (update: DocRecord, origin?: string) => void) {
    this.event.on('update', callback);

    return () => {
      this.event.off('update', callback);
    };
  }

  subscribeLocalDocReplaced(callback: (docId: string) => void) {
    this.event.on('localDocReplaced', callback);
    return () => {
      this.event.off('localDocReplaced', callback);
    };
  }

  /** The stored content of `docId` was replaced or removed outside a commit. */
  protected notifyLocalDocReplaced(docId: string) {
    this.emit('localDocReplaced', docId);
  }

  /**
   * sl-affine-uhr: Subscribe to compaction events.
   * Called when a doc has been compacted on the server.
   * Clients should clear local cache and pull fresh state.
   */
  subscribeDocCompacted(
    callback: (info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }) => void
  ) {
    this.event.on('compacted', callback);
    return () => {
      this.event.off('compacted', callback);
    };
  }

  /**
   * sl-j143.5: Subscribe to permission change events.
   * Called when the user's doc/folder permissions change on the server.
   * Clients should refresh their accessible doc cache.
   */
  subscribePermissionsChanged(
    callback: (info: PermissionsChangedEvent) => void
  ) {
    this.event.on('permissionsChanged', callback);
    return () => {
      this.event.off('permissionsChanged', callback);
    };
  }

  subscribePermissionGraphAdvanced(
    callback: (info: WorkspacePermissionGraphAdvancedPayload) => void
  ) {
    this.event.on('permissionGraphAdvanced', callback);
    return () => {
      this.event.off('permissionGraphAdvanced', callback);
    };
  }

  subscribeDemoContentRemoved(
    callback: (info: DemoContentRemovedEvent) => void
  ) {
    this.event.on('demoContentRemoved', callback);
    return () => {
      this.event.off('demoContentRemoved', callback);
    };
  }

  subscribeSyncProtocolUnsupported(
    callback: (info: SyncProtocolUnsupportedEvent) => void
  ) {
    this.event.on('syncProtocolUnsupported', callback);
    return () => {
      this.event.off('syncProtocolUnsupported', callback);
    };
  }

  subscribeSystemDocAccessReady(
    callback: (info: SystemDocAccessReadyEvent) => void
  ) {
    this.event.on('systemDocAccessReady', callback);
    return () => {
      this.event.off('systemDocAccessReady', callback);
    };
  }

  subscribeDocPersisted(callback: (info: DocPersistedEvent) => void) {
    this.event.on('docPersisted', callback);
    return () => {
      this.event.off('docPersisted', callback);
    };
  }

  /**
   * Subscribe to workspace meta change events.
   * Called when workspace metadata changes (e.g. folder_deleted).
   */
  subscribeWorkspaceMetaChanged(
    callback: (info: {
      workspaceId: string;
      changeType: string;
      docId: string;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }) => void
  ) {
    this.event.on('workspaceMetaChanged', callback);
    return () => {
      this.event.off('workspaceMetaChanged', callback);
    };
  }

  /**
   * sl-lkdb.3.1: Subscribe to recovery completed events.
   * Called when PITR recovery completes — clients should clear IDB and re-sync.
   */
  subscribeRecoveryCompleted(
    callback: (info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }) => void
  ) {
    this.event.on('recoveryCompleted', callback);
    return () => {
      this.event.off('recoveryCompleted', callback);
    };
  }

  /**
   * sl-lv2w.5: Subscribe to doc size warning events (soft limit).
   */
  subscribeDocSizeWarning(
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }) => void
  ) {
    this.event.on('docSizeWarning', callback);
    return () => {
      this.event.off('docSizeWarning', callback);
    };
  }

  /**
   * sl-lv2w.5: Subscribe to doc size hard limit events.
   */
  subscribeDocSizeHardLimit(
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }) => void
  ) {
    this.event.on('docSizeHardLimit', callback);
    return () => {
      this.event.off('docSizeHardLimit', callback);
    };
  }

  /**
   * sl-00ro: Subscribe to embedding doc status events.
   */
  subscribeEmbeddingDocStatus(
    callback: (info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }) => void
  ) {
    this.event.on('embeddingDocStatus', callback);
    return () => {
      this.event.off('embeddingDocStatus', callback);
    };
  }

  /**
   * sl-00ro: Subscribe to embedding workspace progress events.
   */
  subscribeEmbeddingWorkspaceProgress(
    callback: (info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }) => void
  ) {
    this.event.on('embeddingWorkspaceProgress', callback);
    return () => {
      this.event.off('embeddingWorkspaceProgress', callback);
    };
  }

  /**
   * Subscribe to knowledge graph update events.
   */
  subscribeKgUpdated(
    callback: (info: {
      workspaceId: string;
      entityCount: number;
      relationCount: number;
      timestamp: number;
    }) => void
  ) {
    this.event.on('kgUpdated', callback);
    return () => {
      this.event.off('kgUpdated', callback);
    };
  }

  subscribeSearchAdvanced(
    callback: (info: WorkspaceSearchAdvancedPayload) => void
  ) {
    this.event.on('searchAdvanced', callback);
    return () => {
      this.event.off('searchAdvanced', callback);
    };
  }

  // REGION: api for internal usage
  protected on(
    event: 'update',
    callback: (update: DocRecord, origin: string) => void
  ): () => void;
  protected on(
    event: 'snapshot',
    callback: (snapshot: DocRecord, prevSnapshot: DocRecord | null) => void
  ): () => void;
  protected on(
    event: 'corruption',
    callback: (info: DocCorruptionEvent) => void
  ): () => void;
  // sl-affine-uhr: Listen for compaction events
  protected on(
    event: 'compacted',
    callback: (info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }) => void
  ): () => void;
  // sl-j143.5: Listen for permission change events
  protected on(
    event: 'permissionsChanged',
    callback: (info: PermissionsChangedEvent) => void
  ): () => void;
  protected on(
    event: 'permissionGraphAdvanced',
    callback: (info: WorkspacePermissionGraphAdvancedPayload) => void
  ): () => void;
  // Listen for workspace meta change events
  protected on(
    event: 'workspaceMetaChanged',
    callback: (info: {
      workspaceId: string;
      changeType: string;
      docId?: string | null;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }) => void
  ): () => void;
  protected on(
    event: 'demoContentRemoved',
    callback: (info: DemoContentRemovedEvent) => void
  ): () => void;
  protected on(
    event: 'syncProtocolUnsupported',
    callback: (info: SyncProtocolUnsupportedEvent) => void
  ): () => void;
  protected on(
    event: 'systemDocAccessReady',
    callback: (info: SystemDocAccessReadyEvent) => void
  ): () => void;
  protected on(
    event: 'docPersisted',
    callback: (info: DocPersistedEvent) => void
  ): () => void;
  // sl-lkdb.3.1: Listen for recovery completed events
  protected on(
    event: 'recoveryCompleted',
    callback: (info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }) => void
  ): () => void;
  // sl-lv2w.5: Listen for doc size warning events
  protected on(
    event: 'docSizeWarning',
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }) => void
  ): () => void;
  // sl-lv2w.5: Listen for doc size hard limit events
  protected on(
    event: 'docSizeHardLimit',
    callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }) => void
  ): () => void;
  // sl-00ro: Listen for embedding doc status events
  protected on(
    event: 'embeddingDocStatus',
    callback: (info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }) => void
  ): () => void;
  // sl-00ro: Listen for embedding workspace progress events
  protected on(
    event: 'embeddingWorkspaceProgress',
    callback: (info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }) => void
  ): () => void;
  protected on(event: string, callback: (...args: any[]) => void): () => void {
    this.event.on(event, callback);
    return () => {
      this.event.off(event, callback);
    };
  }

  protected emit(event: 'update', update: DocRecord, origin?: string): void;
  protected emit(
    event: 'snapshot',
    snapshot: DocRecord,
    prevSnapshot: DocRecord | null
  ): void;
  protected emit(event: 'corruption', info: DocCorruptionEvent): void;
  // sl-affine-uhr: Emit when a doc has been compacted on the server
  protected emit(
    event: 'compacted',
    info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }
  ): void;
  // sl-j143.5: Emit when user's permissions changed
  protected emit(
    event: 'permissionsChanged',
    info: PermissionsChangedEvent
  ): void;
  protected emit(
    event: 'permissionGraphAdvanced',
    info: WorkspacePermissionGraphAdvancedPayload
  ): void;
  // Emit when workspace metadata changed
  protected emit(
    event: 'workspaceMetaChanged',
    info: {
      workspaceId: string;
      changeType: string;
      docId?: string | null;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }
  ): void;
  protected emit(
    event: 'demoContentRemoved',
    info: DemoContentRemovedEvent
  ): void;
  protected emit(
    event: 'syncProtocolUnsupported',
    info: SyncProtocolUnsupportedEvent
  ): void;
  protected emit(
    event: 'systemDocAccessReady',
    info: SystemDocAccessReadyEvent
  ): void;
  protected emit(event: 'docPersisted', info: DocPersistedEvent): void;
  // sl-lkdb.3.1: Emit when workspace recovery completed
  protected emit(
    event: 'recoveryCompleted',
    info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }
  ): void;
  // sl-lv2w.5: Emit doc size warning
  protected emit(
    event: 'docSizeWarning',
    info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }
  ): void;
  // sl-lv2w.5: Emit doc size hard limit
  protected emit(
    event: 'docSizeHardLimit',
    info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }
  ): void;
  // sl-00ro: Emit embedding doc status
  protected emit(
    event: 'embeddingDocStatus',
    info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }
  ): void;
  // sl-00ro: Emit embedding workspace progress
  protected emit(
    event: 'embeddingWorkspaceProgress',
    info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }
  ): void;
  // Emit knowledge graph updated
  protected emit(
    event: 'kgUpdated',
    info: {
      workspaceId: string;
      entityCount: number;
      relationCount: number;
      timestamp: number;
    }
  ): void;
  // Emit a new search index version of a readable page
  protected emit(
    event: 'searchAdvanced',
    info: WorkspaceSearchAdvancedPayload
  ): void;
  protected emit(event: 'localDocReplaced', docId: string): void;
  protected emit(event: string, ...args: any[]): void {
    this.event.emit(event, ...args);
  }

  protected off(event: string, callback: (...args: any[]) => void): void {
    this.event.off(event, callback);
  }

  /** Snapshot and unmerged updates of a doc; adapters that read both in one
   * transaction override this. */
  protected async getDocParts(
    docId: string
  ): Promise<{ snapshot: DocRecord | null; updates: DocRecord[] }> {
    return {
      snapshot: await this.getDocSnapshot(docId),
      updates: await this.getDocUpdates(docId),
    };
  }

  /**
   * Get a doc snapshot from storage
   */
  protected abstract getDocSnapshot(docId: string): Promise<DocRecord | null>;
  /**
   * Set the doc snapshot into storage
   *
   * @safety
   * be careful when implementing this method.
   *
   * It might be called with outdated snapshot when running in multi-thread environment.
   *
   * A common solution is update the snapshot record is DB only when the coming one's timestamp is newer.
   *
   * @example
   * ```ts
   * await using _lock = await this.lockDocForUpdate(docId);
   * // set snapshot
   *
   * ```
   */
  protected abstract setDocSnapshot(
    snapshot: DocRecord,
    prevSnapshot: DocRecord | null
  ): Promise<boolean>;

  /**
   * Get all updates of a doc that haven't been merged into snapshot.
   *
   * Updates queue design exists for a performace concern:
   * A huge amount of write time will be saved if we don't merge updates into snapshot immediately.
   * Updates will be merged into snapshot when the latest doc is requested.
   */
  protected abstract getDocUpdates(docId: string): Promise<DocRecord[]>;

  /**
   * Mark updates as merged into snapshot.
   */
  protected abstract markUpdatesMerged(
    docId: string,
    updates: DocRecord[]
  ): Promise<number>;

  /**
   * Merge doc updates into a single update.
   */
  protected async squash(updates: DocRecord[]): Promise<DocRecord> {
    const lastUpdate = updates.at(-1);
    if (!lastUpdate) {
      throw new Error('No updates to be squashed.');
    }

    // fast return
    if (updates.length === 1) {
      return lastUpdate;
    }

    const finalUpdate = await this.mergeUpdates(updates.map(u => u.bin));

    return {
      docId: lastUpdate.docId,
      bin: finalUpdate,
      timestamp: lastUpdate.timestamp,
      editor: lastUpdate.editor,
    };
  }

  protected mergeUpdates(updates: Uint8Array[], skipEmpty = true) {
    const merge = this.options?.mergeUpdates ?? mergeUpdates;

    return merge(
      skipEmpty ? updates.filter(bin => !isEmptyUpdate(bin)) : updates
    );
  }

  /** One codec seam for authorized publication bases on every local adapter. */
  protected async mergePublicationParts(
    parts: readonly Uint8Array[]
  ): Promise<Uint8Array | null> {
    if (!parts.length) return null;
    try {
      return await this.mergeUpdates([...parts], false);
    } catch (error) {
      if (error instanceof PublicationError) throw error;
      throw new PublicationError('FORMAT_MISMATCH');
    }
  }

  protected async lockDocForUpdate(docId: string): Promise<AsyncDisposable> {
    return this.locker.lock(`workspace:${this.spaceId}:update`, docId);
  }

  /**
   * Delete only the snapshot for a doc, preserving individual updates.
   * Override in subclasses that support snapshots.
   */
  protected async deleteDocSnapshot(_docId: string): Promise<void> {
    // Default no-op — subclasses with snapshot stores override this
  }

  /**
   * Surgically repair a corrupt doc by validating each IDB entry individually
   * and removing only the corrupt ones. Preserves all valid unsynced data.
   *
   * Algorithm:
   * 1. Read raw snapshot + individual updates (no merge)
   * 2. Validate each with encodeStateVectorFromUpdate (Y.js parse check)
   * 3. Delete only corrupt entries
   * 4. If snapshot was corrupt, rebuild from valid updates or delete
   * 5. Verify the doc is now readable
   */
  async repairDoc(docId: string): Promise<DocRepairResult> {
    await using _lock = await this.lockDocForUpdate(docId);

    const updates = await this.getDocUpdates(docId);

    // Y.js best practice for corrupt local data: nuke and let server re-sync.
    // Individual entry validation is unreliable — entries can be individually
    // valid but produce "Integer out of Range" when merged.
    // See: https://discuss.yjs.dev/t/caught-error-while-handling-a-yjs-update-rangeerror/1789
    await this.deleteDocSnapshot(docId);
    if (updates.length > 0) {
      await this.markUpdatesMerged(docId, updates);
    }
    this.notifyLocalDocReplaced(docId);

    console.warn(
      `[DocStorage:repair] Cleared all local data for ${docId} ` +
        `(snapshot + ${updates.length} updates). Server will re-sync.`
    );

    return {
      kept: 0,
      removed: updates.length,
      snapshotRemoved: true,
      docReadable: false,
    };
  }
}
